// packages/console/src/components/panels/JobQueue.tsx
//
// Job queue: in-flight uploads (FT-2) shown above transcode jobs (FT-3), with
// live per-row progress/speed/eta from the FT-4 jobs stream. Filters are a
// client filter over the job list. Every simulated value from the prototype is
// removed (R10(b)): job progress rides the stream, queued rows show the em dash
// for progress/speed/eta (R1), the subtitle uses the real failure reason, and
// there is no index-based worker assignment.

import { useState } from "react";
import { Meter, Dot, type Tone } from "../Parts";
import { Icon } from "../Icons";
import { PlayerOverlay } from "../PlayerOverlay";
import { EM_DASH, fmtDur, fmtEta, fmtGB } from "../../lib/format";
import { fetchOutputUrl, type Container, type Job, type JobState, type Preset } from "../../api/jobs";
import type { Source } from "../../api/sources";
import type { JobStreamEvent } from "../../api/telemetry";
import type { UploadView } from "../../upload/engine";
import type { DeliveryTarget } from "../../api/deliveries";
import type { StagedSelection } from "../FileTranscoder";
import { CONTAINERS, packagingOptionsFor, defaultPackagingFor } from "../../lib/preset";

// StagedSource is a landed upload awaiting a Start action (upload/transcode are
// decoupled). It mirrors the shape FileTranscoder holds; the queue shows one row
// per staged source with a Start button. sha256 is null when a from-URL landing
// has not surfaced a hash yet (the Library owns delete in that case).
export interface StagedSource {
  objectKey: string;
  sha256: string | null;
  filename: string;
}

// The Profile column is sized to fit a full 20-character profile name without
// truncation at the 12px sans/mono readout used in this table (~8px per glyph
// plus breathing room), so long profile names read in full.
const GRID = "1fr 168px 70px 124px 52px 62px 72px";
// STAGED_GRID lays out a staged (ready-to-start) row as its own one-line
// pipeline: the title (ellipsised), then the three compact inline selects
// (Profile wider, Packaging + Output narrower), then the Start button. It is
// visually distinct from the running/done job grid above, which keeps its
// per-column progress/speed/eta readout.
const STAGED_GRID = "1fr 150px 96px 116px 74px";
type Filter = "all" | "running" | "queued" | "done" | "failed";

const STATE_LABEL: Record<JobState, [Tone, string]> = {
  completed: ["ok", "Complete"],
  running: ["onair", "Encoding"],
  queued: ["idle", "Queued"],
  failed: ["err", "Failed"],
};

// rungRank parses the resolution height from a ladder rung name ("360p" -> 360,
// "1080p" -> 1080, "2160p" -> 2160). Names without a number rank 0. Used to pick
// the best-quality output deterministically rather than relying on array order.
function rungRank(name: string): number {
  const m = name.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}

// readyOutputs returns every completed ladder output with a stored object key,
// highest-resolution first, so the job row can offer one "open" chip per rung.
function readyOutputs(job: Job): string[] {
  return job.outputs
    .filter((o) => o.state === "completed" && o.objectKey)
    .map((o) => o.name)
    .sort((a, b) => rungRank(b) - rungRank(a));
}

// forceDownload saves the encoded output via a short-lived presigned URL that the
// server signs with an attachment Content-Disposition (so the browser downloads
// rather than plays inline). On failure the error is surfaced (never a silent
// success, R10).
async function forceDownload(job: Job, rung: string, format?: Container): Promise<void> {
  try {
    const { url } = await fetchOutputUrl(job.id, rung, { download: true, format });
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (err) {
    window.alert(`Could not download the output: ${err instanceof Error ? err.message : "request failed"}`);
  }
}

// basename returns the last path segment of an object key, the human fallback
// for a row's name when no filename is recorded (matches the job-row fallback).
function basename(objectKey: string): string {
  return objectKey.split("/").pop() ?? objectKey;
}

const FILTER_TO_STATE: Record<Exclude<Filter, "all">, JobState> = {
  running: "running",
  queued: "queued",
  done: "completed",
  failed: "failed",
};

export function JobQueue({
  jobs,
  presets,
  uploads,
  progress,
  filter,
  onFilter,
  sel,
  onSelJob,
  xfer,
  onSelXfer,
  onRetry,
  onCancel,
  loading,
  errorMessage,
  staged,
  onStartJob,
  inlinePresets,
  stagedSel,
  onStagedSelChange,
  deliveryTargets,
  sources,
  sourcesLoading,
  sourcesError,
  onRequeue,
  onDeleteSource,
  libraryOpen,
  onLibraryOpen,
}: {
  jobs: Job[];
  presets: Preset[];
  uploads: UploadView[];
  progress: Map<string, JobStreamEvent>;
  filter: Filter;
  onFilter: (f: Filter) => void;
  sel: string | null;
  onSelJob: (id: string) => void;
  xfer: string | null;
  onSelXfer: (id: string) => void;
  onRetry: (id: string) => void;
  onCancel: (id: string) => void;
  loading: boolean;
  errorMessage: string | null;
  // Staged sources: landed uploads awaiting a Start action (upload/transcode
  // decoupled). Shown as selectable rows in the queue view with a Start button.
  staged: StagedSource[];
  onStartJob: (item: StagedSource) => void;
  // inlinePresets are every profile offered by the per-row Profile select,
  // grouped Standard → EVE → Custom. stagedSel holds each row's {presetId,
  // packaging, output} choice, keyed by objectKey; onStagedSelChange patches one
  // row's choice. deliveryTargets fills the Output select (empty = delivery not
  // enabled, so Output shows only the local "Media library").
  inlinePresets: Preset[];
  stagedSel: Record<string, StagedSelection>;
  onStagedSelChange: (objectKey: string, patch: Partial<StagedSelection>) => void;
  deliveryTargets: DeliveryTarget[];
  // Library: the workspace's landed + probed sources, re-stageable or deletable.
  sources: Source[];
  sourcesLoading: boolean;
  sourcesError: string | null;
  onRequeue: (source: Source) => void;
  onDeleteSource: (sha: string) => void;
  // The queue/library view is lifted so a re-stage from the Library switches back.
  libraryOpen: boolean;
  onLibraryOpen: (open: boolean) => void;
}) {
  const presetById = new Map(presets.map((p) => [p.id, p]));
  const shown = filter === "all" ? jobs : jobs.filter((j) => j.state === FILTER_TO_STATE[filter]);
  // view is lifted (libraryOpen): "library" swaps the job list for the source
  // library, "queue" is the default job + upload + staged list.
  const view: "queue" | "library" = libraryOpen ? "library" : "queue";
  const [playJob, setPlayJob] = useState<Job | null>(null);

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        background: "var(--bg-panel)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-md)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", borderBottom: "1px solid var(--line)", flex: "none" }}>
        <span style={{ font: "var(--t-eyebrow)", letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase", color: "var(--fg3)" }}>
          Job queue
        </span>
        <div style={{ flex: 1 }} />
        {/* View selector: the status filters and Library are ONE mutually
            exclusive radio group, exactly one is highlighted at a time. The
            status chips stay VISIBLE when the Library is open, but all read as
            deselected (only Library is highlighted), so the way back to the job
            queue is always obvious. Clicking any status chip while the Library
            is open closes it and shows the queue with that status selected. */}
        <div style={{ display: "flex", gap: 6 }}>
          {([
            ["all", "All"],
            ["running", "Running"],
            ["queued", "Queued"],
            ["done", "Done"],
            ["failed", "Failed"],
          ] as [Filter, string][]).map(([k, l]) => {
            // Highlight a status chip only in the queue view; in the Library
            // view every status chip is deselected (Library owns the highlight).
            const on = view !== "library" && filter === k;
            return (
              <button
                key={k}
                className="ae-b"
                onClick={() => {
                  onLibraryOpen(false);
                  onFilter(k);
                }}
                style={{
                  font: "600 10px var(--font-sans)",
                  letterSpacing: ".09em",
                  textTransform: "uppercase",
                  padding: "6px 10px",
                  borderRadius: "var(--r-xs)",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  background: on ? "var(--blue-tint)" : "transparent",
                  border: `1px solid ${on ? "var(--blue-500)" : "var(--line-strong)"}`,
                  color: on ? "var(--blue-300)" : "var(--fg2)",
                }}
              >
                {l}
              </button>
            );
          })}
          {/* Library: the last member of the same radio group. Highlighted only
              when the library view is open; the status chips stay visible but
              deselected so the queue is one click away. */}
          <button
            className="ae-b"
            onClick={() => onLibraryOpen(view !== "library")}
            title="Browse landed sources (re-stage or delete)"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              font: "600 10px var(--font-sans)",
              letterSpacing: ".09em",
              textTransform: "uppercase",
              padding: "6px 10px",
              borderRadius: "var(--r-xs)",
              cursor: "pointer",
              whiteSpace: "nowrap",
              background: view === "library" ? "var(--blue-tint)" : "transparent",
              border: `1px solid ${view === "library" ? "var(--blue-500)" : "var(--line-strong)"}`,
              color: view === "library" ? "var(--blue-300)" : "var(--fg2)",
            }}
          >
            <Icon name="layers" size={12} />
            Library
          </button>
        </div>
      </div>

      {view === "library" ? (
        <SourceLibrary
          sources={sources}
          loading={sourcesLoading}
          errorMessage={sourcesError}
          onRequeue={onRequeue}
          onDeleteSource={onDeleteSource}
        />
      ) : (
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: GRID,
            alignItems: "center",
            gap: 10,
            padding: "0 13px",
            height: 32,
            borderBottom: "1px solid var(--line)",
            font: "500 9px var(--font-sans)",
            letterSpacing: ".13em",
            textTransform: "uppercase",
            color: "var(--fg4)",
            position: "sticky",
            top: 0,
            background: "var(--bg-panel)",
            zIndex: 2,
          }}
        >
          <span>Source</span>
          <span>Profile</span>
          <span>Duration</span>
          <span>Progress</span>
          <span>Speed</span>
          <span>Eta</span>
          <span>State</span>
        </div>

        {/* Staged rows: landed uploads (or Library requeues) awaiting a Start
            action. Each row is a one-line pipeline, title, then inline Profile /
            Packaging / Output selects, then Start, configured independently. */}
        {staged.map((s) => (
          <StagedRow
            key={`staged:${s.objectKey}`}
            item={s}
            selected={sel === s.objectKey}
            onSelect={() => onSelJob(s.objectKey)}
            onStart={() => onStartJob(s)}
            presets={inlinePresets}
            sel={stagedSel[s.objectKey]}
            onChange={(patch) => onStagedSelChange(s.objectKey, patch)}
            deliveryTargets={deliveryTargets}
          />
        ))}

        {/* Upload rows (only in the All filter, matching the prototype). */}
        {filter === "all" &&
          uploads.map((u) => {
            const upct = u.chunkCount > 0 ? (u.doneChunks / u.chunkCount) * 100 : 0;
            const rateMBs = u.throughputBytesPerSec / (1024 * 1024);
            return (
              <div
                key={u.id}
                onClick={() => onSelXfer(u.id)}
                style={{
                  display: "grid",
                  gridTemplateColumns: GRID,
                  alignItems: "center",
                  gap: 10,
                  padding: "0 13px",
                  minHeight: 46,
                  cursor: "pointer",
                  borderBottom: "1px solid var(--line)",
                  background: xfer === u.id ? "var(--blue-tint)" : "transparent",
                  boxShadow: xfer === u.id ? "inset 2px 0 0 var(--blue-500)" : "none",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <span style={{ font: "400 12px var(--font-mono)", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {u.file}
                  </span>
                  <span style={{ display: "flex", alignItems: "baseline", gap: 5, whiteSpace: "nowrap", overflow: "hidden" }}>
                    {u.state === "error" ? (
                      <span style={{ font: "var(--t-micro)", color: "var(--err)" }}>link lost · resuming from chunk map</span>
                    ) : (
                      <>
                        <span style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>
                          uploading · {u.doneChunks}/{u.chunkCount} chunks ·
                        </span>
                        <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)", fontVariantNumeric: "tabular-nums" }}>
                          {fmtGB(u.sizeBytes)}
                        </span>
                      </>
                    )}
                  </span>
                </div>
                <span style={{ font: "var(--t-body-sm)", color: "var(--fg4)" }}>{EM_DASH}</span>
                <span style={{ font: "400 12px var(--font-mono)", color: "var(--fg4)" }}>{EM_DASH}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ font: "400 12px var(--font-mono)", color: "#fff", width: 34, fontVariantNumeric: "tabular-nums" }}>
                    {upct.toFixed(0)}%
                  </span>
                  <div style={{ flex: 1 }}>
                    <Meter pct={upct} color={u.state === "error" ? "var(--err)" : u.state === "verifying" ? "var(--warn)" : "var(--viz-2)"} />
                  </div>
                </div>
                <span style={{ font: "400 12px var(--font-mono)", color: "var(--fg2)", fontVariantNumeric: "tabular-nums" }}>
                  {rateMBs > 0 ? rateMBs.toFixed(0) : EM_DASH}
                </span>
                <span style={{ font: "400 12px var(--font-mono)", color: "var(--fg4)" }}>{EM_DASH}</span>
                <span style={{ display: "flex", alignItems: "center", gap: 7, font: "var(--t-micro)", color: "var(--fg2)" }}>
                  <Dot
                    tone={u.state === "error" ? "err" : u.state === "verifying" ? "warn" : u.state === "paused" ? "idle" : "ok"}
                    pulse={u.state === "uploading"}
                  />
                  {u.state === "verifying" ? "Verifying" : u.state === "paused" ? "Paused" : u.state === "error" ? "Resuming" : "Uploading"}
                </span>
              </div>
            );
          })}

        {/* Job rows. */}
        {shown.map((j) => {
          const [tone, label] = STATE_LABEL[j.state];
          const live = progress.get(j.id);
          const pct = j.state === "running" && live ? live.progressPct : j.progressPct;
          const speed = j.state === "running" && live ? live.speedX : j.speedX;
          const eta = j.state === "running" && live ? live.etaSeconds : j.etaSeconds;
          const preset = presetById.get(j.presetId);
          const showPct = j.state !== "queued";
          const showSpeed = j.state === "running" && speed > 0;
          const showEta = j.state === "running" && eta > 0;
          return (
            <div
              key={j.id}
              onClick={() => onSelJob(j.id)}
              style={{
                display: "grid",
                gridTemplateColumns: GRID,
                alignItems: "center",
                gap: 10,
                padding: "0 13px",
                minHeight: 46,
                cursor: "pointer",
                borderBottom: "1px solid var(--line)",
                background: sel === j.id ? "var(--blue-tint)" : "transparent",
                boxShadow: sel === j.id ? "inset 2px 0 0 var(--blue-500)" : "none",
                opacity: j.state === "completed" ? 0.62 : 1,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ font: "400 12px var(--font-mono)", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {j.sourceFilename || (j.sourceObjectKey.split("/").pop() ?? j.sourceObjectKey)}
                </span>
                <span
                  style={{
                    font: "var(--t-micro)",
                    color: j.errorMessage ? "var(--err)" : "var(--fg4)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {j.errorMessage
                    ? `${j.errorClass ? j.errorClass + " · " : ""}${j.errorMessage}`
                    : `attempt ${j.attempts} · ${preset ? preset.container.toUpperCase() : EM_DASH}`}
                </span>
              </div>
              <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {preset ? preset.name : EM_DASH}
              </span>
              <span style={{ font: "400 12px var(--font-mono)", color: "var(--fg3)", fontVariantNumeric: "tabular-nums" }}>
                {/* Duration is probe truth (FT-3), not on the job record: em dash. */}
                {EM_DASH}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span
                  style={{
                    font: "400 12px var(--font-mono)",
                    color: showPct ? "#fff" : "var(--fg4)",
                    width: 34,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {showPct ? `${Math.round(pct)}%` : EM_DASH}
                </span>
                <div style={{ flex: 1 }}>
                  <Meter
                    pct={pct}
                    color={
                      j.state === "failed"
                        ? "var(--err)"
                        : j.state === "completed"
                          ? "var(--ok)"
                          : j.state === "running"
                            ? "var(--blue-500)"
                            : "var(--idle)"
                    }
                  />
                </div>
              </div>
              <span style={{ font: "400 12px var(--font-mono)", color: "var(--fg2)", fontVariantNumeric: "tabular-nums" }}>
                {showSpeed ? `${speed.toFixed(1)}×` : EM_DASH}
              </span>
              <span style={{ font: "400 12px var(--font-mono)", color: "var(--fg2)", fontVariantNumeric: "tabular-nums" }}>
                {showEta ? fmtEta(eta) : EM_DASH}
              </span>
              {j.state === "failed" ? (
                <button
                  className="ae-b"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRetry(j.id);
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    font: "600 10px var(--font-sans)",
                    letterSpacing: ".09em",
                    textTransform: "uppercase",
                    padding: "5px 8px",
                    borderRadius: "var(--r-xs)",
                    cursor: "pointer",
                    background: "transparent",
                    border: "1px solid var(--line-strong)",
                    color: "var(--fg1)",
                  }}
                >
                  <Icon name="rotate-ccw" size={11} />
                  Retry
                </button>
              ) : j.state === "completed" && readyOutputs(j).length > 0 ? (
                <div style={{ display: "flex", alignItems: "center", gap: 5, justifyContent: "flex-end" }}>
                  <button
                    className="ae-b"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPlayJob(j);
                    }}
                    title="Preview in the browser"
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 26, borderRadius: "var(--r-xs)", cursor: "pointer", background: "transparent", border: "1px solid var(--line-strong)", color: "var(--fg1)" }}
                  >
                    <Icon name="play" size={12} />
                  </button>
                  <button
                    className="ae-b"
                    onClick={(e) => {
                      e.stopPropagation();
                      void forceDownload(j, readyOutputs(j)[0]);
                    }}
                    title="Download the output"
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 26, borderRadius: "var(--r-xs)", cursor: "pointer", background: "transparent", border: "1px solid var(--line-strong)", color: "var(--fg1)" }}
                  >
                    <Icon name="upload" size={12} style={{ transform: "rotate(180deg)" }} />
                  </button>
                </div>
              ) : j.state === "running" || j.state === "queued" ? (
                <button
                  className="ae-b"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(j.id);
                  }}
                  title="Cancel this job"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    font: "600 10px var(--font-sans)",
                    letterSpacing: ".09em",
                    textTransform: "uppercase",
                    padding: "5px 8px",
                    borderRadius: "var(--r-xs)",
                    cursor: "pointer",
                    background: "transparent",
                    border: "1px solid var(--line-strong)",
                    color: "var(--fg1)",
                  }}
                >
                  <Icon name="x" size={11} />
                  Cancel
                </button>
              ) : (
                // Reached only by a completed job whose outputs are not yet
                // listable (running/queued/failed are handled above), so it is
                // never the pulsing running state.
                <span style={{ display: "flex", alignItems: "center", gap: 7, font: "var(--t-micro)", color: "var(--fg2)" }}>
                  <Dot tone={tone} pulse={false} />
                  {label}
                </span>
              )}
            </div>
          );
        })}

        {/* Honest empty / loading / error states (R10). When there is genuinely
            nothing yet (no jobs, no uploads, no staged rows) and the list is not
            loading/errored, show the drop-zone graphic instead of a bare line, the real drag/drop handler lives on the container in FileTranscoder;
            this is purely the visual empty state. */}
        {shown.length === 0 && uploads.length === 0 && staged.length === 0 && (
          loading || errorMessage ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--fg4)", font: "var(--t-micro)" }}>
              {loading ? "Loading jobs..." : `Jobs unavailable: ${errorMessage}`}
            </div>
          ) : (
            <div style={{ padding: 24, display: "flex", justifyContent: "center" }}>
              <div
                style={{
                  width: "100%",
                  maxWidth: 460,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 12,
                  padding: "44px 24px",
                  border: "1px dashed var(--line-strong)",
                  borderRadius: "var(--r-md)",
                  background: "var(--bg-void)",
                  textAlign: "center",
                }}
              >
                <Icon name="upload-cloud" size={34} color="var(--fg3)" />
                <span style={{ font: "var(--t-h3)", letterSpacing: "var(--ls-head)", color: "var(--fg2)" }}>
                  Drop files here to upload
                </span>
                <span style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>
                  or use Add media / From URL
                </span>
              </div>
            </div>
          )
        )}
      </div>
      )}

      {playJob && (
        <PlayerOverlay
          job={playJob}
          rungs={readyOutputs(playJob)}
          onClose={() => setPlayJob(null)}
          onDownload={(rung, format) => void forceDownload(playJob, rung, format)}
        />
      )}
    </div>
  );
}

// selectStyle is the shared compact style for the three inline pipeline selects
// (native <select>, so the option list and keyboard behavior are the platform's).
const selectStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 30,
  padding: "0 6px",
  borderRadius: "var(--r-xs)",
  cursor: "pointer",
  font: "400 11px var(--font-mono)",
  color: "var(--fg1)",
  background: "var(--bg-void)",
  border: "1px solid var(--line-strong)",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// StagedRow is one staged title's inline pipeline: the name, then the Profile,
// Packaging and Output selects, then Start. Each row reads and writes its own
// {presetId, packaging, output} entry (via sel/onChange). Changing the Profile
// resets Packaging to a container valid for the new profile's codec. Selecting a
// profile/packaging captured here rides into Start unchanged (the parent snapshots
// the selection at click). deliveryTargets fills Output; empty = just "Media
// library" (value "" = local, no external push) because delivery is gated off.
function StagedRow({
  item,
  selected,
  onSelect,
  onStart,
  presets,
  sel,
  onChange,
  deliveryTargets,
}: {
  item: StagedSource;
  selected: boolean;
  onSelect: () => void;
  onStart: () => void;
  presets: Preset[];
  sel: StagedSelection | undefined;
  onChange: (patch: Partial<StagedSelection>) => void;
  deliveryTargets: DeliveryTarget[];
}) {
  const presetId = sel?.presetId ?? null;
  const packaging = sel?.packaging ?? "";
  const output = sel?.output ?? "";
  const selectedPreset = presets.find((p) => p.id === presetId) ?? null;

  // Packaging options = the containers the selected profile's codec can carry.
  // Empty when no profile is resolved yet (the select is disabled then).
  const packagingOpts = selectedPreset ? packagingOptionsFor(selectedPreset.videoCodec) : [];
  const labelFor = (c: Container) => CONTAINERS.find((x) => x.id === c)?.label ?? c.toUpperCase();

  // Split the profile options into Standard (builtin, non-EVE), EVE (eve=true),
  // and Custom (user-authored, non-EVE) for the three optgroups. EVE profiles
  // carry the correct ladders and run today as ordinary encodes.
  const standard = presets.filter((p) => p.builtin && !p.eve);
  const eveProfiles = presets.filter((p) => p.eve);
  const custom = presets.filter((p) => !p.builtin && !p.eve);

  // Changing the profile resets Packaging to a valid default for the new codec,
  // so the row never carries a container the new profile's codec cannot produce.
  const onProfile = (id: string) => {
    const next = presets.find((p) => p.id === id) ?? null;
    onChange({ presetId: id, packaging: next ? defaultPackagingFor(next) : "mp4" });
  };

  // canStart guards Start until a real profile is resolved (a brand-new
  // workspace may seed its default profile server-side after the row lands).
  const canStart = presetId !== null;

  return (
    <div
      onClick={onSelect}
      style={{
        display: "grid",
        gridTemplateColumns: STAGED_GRID,
        alignItems: "center",
        gap: 8,
        padding: "0 13px",
        minHeight: 46,
        cursor: "pointer",
        borderBottom: "1px solid var(--line)",
        background: selected ? "var(--blue-tint)" : "transparent",
        boxShadow: selected ? "inset 2px 0 0 var(--blue-500)" : "none",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <span style={{ font: "400 12px var(--font-mono)", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.filename || basename(item.objectKey)}
        </span>
        <span style={{ font: "var(--t-micro)", color: "var(--fg4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          staged · ready to start
        </span>
      </div>

      {/* Profile: every workspace profile, grouped Standard → EVE → Custom. */}
      <select
        value={presetId ?? ""}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onProfile(e.target.value)}
        title="Output profile"
        aria-label="Output profile"
        style={selectStyle}
        disabled={presets.length === 0}
      >
        {presets.length === 0 && <option value="">No profiles</option>}
        {standard.length > 0 && (
          <optgroup label="Standard">
            {standard.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </optgroup>
        )}
        {eveProfiles.length > 0 && (
          <optgroup label="EVE">
            {eveProfiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </optgroup>
        )}
        {custom.length > 0 && (
          <optgroup label="Custom">
            {custom.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </optgroup>
        )}
      </select>

      {/* Packaging: the delivery containers the selected profile's codec allows. */}
      <select
        value={packaging}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onChange({ packaging: e.target.value })}
        title="Packaging (delivery container)"
        aria-label="Packaging"
        style={selectStyle}
        disabled={packagingOpts.length === 0}
      >
        {packagingOpts.map((c) => (
          <option key={c} value={c}>
            {labelFor(c)}
          </option>
        ))}
      </select>

      {/* Output: delivery targets, or just "Media library" when delivery is off. */}
      <select
        value={output}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onChange({ output: e.target.value })}
        title="Output destination"
        aria-label="Output destination"
        style={selectStyle}
      >
        <option value="">Media library</option>
        {deliveryTargets.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>

      <button
        className="ae-b"
        onClick={(e) => {
          e.stopPropagation();
          onStart();
        }}
        disabled={!canStart}
        title={canStart ? "Start the transcode with this pipeline" : "Waiting for an output profile"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
          font: "600 10px var(--font-sans)",
          letterSpacing: ".09em",
          textTransform: "uppercase",
          padding: "5px 8px",
          borderRadius: "var(--r-xs)",
          cursor: canStart ? "pointer" : "not-allowed",
          color: "#fff",
          background: canStart ? "var(--blue-500)" : "var(--line-strong)",
          border: "1px solid transparent",
        }}
      >
        <Icon name="play" size={11} />
        Start
      </button>
    </div>
  );
}

const LIB_GRID = "1fr 120px 96px 140px 150px";

// SourceLibrary lists the workspace's landed + probed sources (upload/transcode
// decoupled). Each row shows the source name, size, container, and duration from
// the probe, with Requeue (re-stage) and Delete (by sha256) actions. Empty and
// loading states are honest (R10): nothing is fabricated for an unprobed source.
function SourceLibrary({
  sources,
  loading,
  errorMessage,
  onRequeue,
  onDeleteSource,
}: {
  sources: Source[];
  loading: boolean;
  errorMessage: string | null;
  onRequeue: (source: Source) => void;
  onDeleteSource: (sha: string) => void;
}) {
  return (
    <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: LIB_GRID,
          alignItems: "center",
          gap: 10,
          padding: "0 13px",
          height: 32,
          borderBottom: "1px solid var(--line)",
          font: "500 9px var(--font-sans)",
          letterSpacing: ".13em",
          textTransform: "uppercase",
          color: "var(--fg4)",
          position: "sticky",
          top: 0,
          background: "var(--bg-panel)",
          zIndex: 2,
        }}
      >
        <span>Source</span>
        <span>Container</span>
        <span>Size</span>
        <span>Duration</span>
        <span style={{ textAlign: "right" }}>Actions</span>
      </div>

      {sources.map((s) => (
        <div
          key={s.sha256 || s.objectKey}
          style={{
            display: "grid",
            gridTemplateColumns: LIB_GRID,
            alignItems: "center",
            gap: 10,
            padding: "0 13px",
            minHeight: 46,
            borderBottom: "1px solid var(--line)",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <span style={{ font: "400 12px var(--font-mono)", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {s.filename || basename(s.objectKey)}
            </span>
            <span style={{ font: "var(--t-micro)", color: "var(--fg4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {s.mime || EM_DASH}
            </span>
          </div>
          <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {s.mediaInfo?.container ? s.mediaInfo.container.toUpperCase() : EM_DASH}
          </span>
          <span style={{ font: "400 12px var(--font-mono)", color: "var(--fg2)", fontVariantNumeric: "tabular-nums" }}>
            {fmtGB(s.sizeBytes)}
          </span>
          <span style={{ font: "400 12px var(--font-mono)", color: "var(--fg3)", fontVariantNumeric: "tabular-nums" }}>
            {s.mediaInfo && s.mediaInfo.durationSeconds > 0 ? fmtDur(s.mediaInfo.durationSeconds) : EM_DASH}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 5, justifyContent: "flex-end" }}>
            <button
              className="ae-b"
              onClick={() => onRequeue(s)}
              title="Re-stage this source in the queue"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                font: "600 10px var(--font-sans)",
                letterSpacing: ".09em",
                textTransform: "uppercase",
                padding: "5px 8px",
                borderRadius: "var(--r-xs)",
                cursor: "pointer",
                background: "transparent",
                border: "1px solid var(--line-strong)",
                color: "var(--fg1)",
              }}
            >
              <Icon name="rotate-ccw" size={11} />
              Requeue
            </button>
            <button
              className="ae-b"
              onClick={() => {
                if (window.confirm(`Delete source "${s.filename || basename(s.objectKey)}"? This cannot be undone.`)) {
                  onDeleteSource(s.sha256);
                }
              }}
              title="Delete this source"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                font: "600 10px var(--font-sans)",
                letterSpacing: ".09em",
                textTransform: "uppercase",
                padding: "5px 8px",
                borderRadius: "var(--r-xs)",
                cursor: "pointer",
                background: "transparent",
                border: "1px solid var(--line-strong)",
                color: "var(--fg1)",
              }}
            >
              <Icon name="x" size={11} />
              Delete
            </button>
          </div>
        </div>
      ))}

      {sources.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: "var(--fg4)", font: "var(--t-micro)" }}>
          {loading ? "Loading library..." : errorMessage ? `Library unavailable: ${errorMessage}` : "No landed sources yet. Add media to stage one."}
        </div>
      )}
    </div>
  );
}
